import React, { useState, useMemo } from "react";
import "../../styles/basket-footer.scss";
import "../../styles/confirm-client-details.scss";
import TextInput from "../inputs/textInput";
import PrimaryButton from "../buttons/primaryButton";
import PaymentProcessorSwitch from "../shared/PaymentProcessorSwitch";
import { useTranslation, Trans } from "react-i18next";
import * as rpc from "../../../common/jsonRpcRoutes";
import { CUSTOMER_NOTES_MAX_LENGTH } from "../../../common/dietaryRequirements";
import { isAbortedRequest, useRpcCaller } from "../context/WidgetRequestContext";
import { ExclamationCircle, InfoCircle } from "../icons";
import AlertBanner from "../shared/AlertBanner";
import CreditCardDisabledNotice from "../shared/CreditCardDisabledNotice";
import { isCreditCardPaymentDisabledForBooking } from "../../utils/paymentHelpers";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { getMismatchedPriceRulesCount } from "./preOrderUtils";

interface ConfirmClientDetailsProps {
  name?: string;
  email?: string;
  customerNotes?: string;
  /**
   * Notified with the order notes whenever the guest confirms the details step,
   * so the dietary requirements' shared order note stays in sync with edits
   * made here.
   */
  onCustomerNotesChange?: (customerNotes: string) => void;
  onSubmit: (details: { name: string; email: string; customerNotes?: string }) => Promise<void>;
  paidByGroupMember: boolean;
  bookingData: any;
  customerPreOrder: any;
  useStripeConnect: boolean;
  onPaymentSuccess?: (email?: string, amount?: number) => void;
  /**
   * Optional controlled "confirmed" state. When provided (with `setIsConfirmed`),
   * the parent owns the confirmed/payment-form sub-step so that an external back
   * button (e.g. the header/MobileStepNav) can step back from the payment form to
   * the editable details view without unmounting the component. When omitted, the
   * component manages this state internally.
   */
  isConfirmed?: boolean;
  setIsConfirmed?: (value: boolean) => void;
  confirmDisabled?: boolean;
}

const ConfirmClientDetails: React.FC<ConfirmClientDetailsProps> = ({
  name = "",
  email = "",
  customerNotes = "",
  onCustomerNotesChange,
  onSubmit,
  paidByGroupMember,
  bookingData,
  customerPreOrder,
  useStripeConnect,
  onPaymentSuccess,
  isConfirmed: isConfirmedProp,
  setIsConfirmed: setIsConfirmedProp,
  confirmDisabled = false,
}) => {
  const { t } = useTranslation();
  const call = useRpcCaller();
  const [clientName, setClientName] = useState(name);
  const [clientEmail, setClientEmail] = useState(email);
  const [clientCustomerNotes, setClientCustomerNotes] = useState(customerNotes);
  const [showNotesField, setShowNotesField] = useState(!!customerNotes);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isConfirmedInternal, setIsConfirmedInternal] = useState(false);
  // Support both controlled (parent-owned) and uncontrolled (internal) confirmed state.
  const isConfirmed = isConfirmedProp ?? isConfirmedInternal;
  const setIsConfirmed = setIsConfirmedProp ?? setIsConfirmedInternal;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Collins refuses credit-funded cards for this booking type, so warn the guest
  // before they enter card details and let the payment form refuse the card
  // before it is charged.
  const creditCardsDisabled = isCreditCardPaymentDisabledForBooking(bookingData);

  const validateClientDetails = React.useCallback((name: string, email: string): boolean => {
    return !(!name || !email || !/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(email));
  }, []);

  const handleSubmit = async () => {
    if (confirmDisabled) return;
    if (!validateClientDetails(clientName, clientEmail)) {
      setShowValidationErrors(true);
      return;
    }
    setShowValidationErrors(false);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      onCustomerNotesChange?.(clientCustomerNotes);
      await onSubmit({ name: clientName, email: clientEmail, customerNotes: clientCustomerNotes });
    } catch (error) {
      if (isAbortedRequest(error)) return;
      const serverMessage = error instanceof rpc.RpcError ? error.bodyMessage : undefined;
      setSubmitError(
        serverMessage ??
          t("preorder_save_failed", "Sorry, we could not save your order. Please try again or contact the venue."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDetails = () => {
    if (confirmDisabled) return;
    if (!validateClientDetails(clientName, clientEmail)) {
      setShowValidationErrors(true);
      return;
    }
    setShowValidationErrors(false);
    onCustomerNotesChange?.(clientCustomerNotes);

    if (paidByGroupMember && bookingData?.bookingId) {
      call("/storeCustomerDetails", {
        booking_id: bookingData.bookingId,
        email: clientEmail,
        name: clientName,
        customer_notes: clientCustomerNotes,
      }).catch((error) => {
        if (isAbortedRequest(error)) return;
        console.error("Error caching customer details:", error);
      });
    }

    setIsConfirmed(true);
  };

  // Compute error messages
  const errorMessages: string[] = [];
  if (!clientName) errorMessages.push(t("name_required", "Name is required"));
  if (!clientEmail) errorMessages.push(t("email_required", "Email is required"));
  if (clientEmail && !/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(clientEmail))
    errorMessages.push(t("invalid_email", "Please enter a valid email address"));

  const isValidDetails = validateClientDetails(clientName, clientEmail);
  const hasPendingOrder = !!customerPreOrder?.pendingPreorderId;
  const showPaymentForm = paidByGroupMember && hasPendingOrder && isConfirmed && isValidDetails;

  const numMismatchedPriceRules = useMemo(
    () => getMismatchedPriceRulesCount(customerPreOrder?.items ?? [], customerPreOrder?.menus ?? []),
    [customerPreOrder?.items, customerPreOrder?.menus],
  );

  const renderActionButton = () => {
    if (!paidByGroupMember) {
      return (
        <PrimaryButton
          type="button"
          onClick={() => void handleSubmit()}
          className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button gcw:w-full"
          disabled={isSubmitting || confirmDisabled}
          text={isSubmitting ? t("confirming", "Confirming...") : t("confirm", "Confirm")}
        />
      );
    }

    if (!hasPendingOrder) {
      return <span>{t("no_items_selected", "No items are selected")}</span>;
    }

    if (isConfirmed) {
      if (!isValidDetails) {
        return (
          <PrimaryButton
            text={t("complete_preorder", "Complete Details to Continue")}
            className={
              "gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button gcw:w-full"
            }
            onClick={() => setShowValidationErrors(true)}
            disabled={confirmDisabled}
          />
        );
      }

      return (
        <>
          {creditCardsDisabled && <CreditCardDisabledNotice className="gcw:mb-bs-3 gcw:w-full" />}
          <PaymentProcessorSwitch
            useStripeConnect={useStripeConnect}
            stripeProps={{
              action: "place-your-order",
              pendingPreorderId: customerPreOrder?.pendingPreorderId,
              bookingData,
              bookingId: bookingData.bookingId,
              amount:
                customerPreOrder?.requiredPaymentGross ??
                customerPreOrder?.requiredPaymentAmount ??
                customerPreOrder?.total_gross ??
                customerPreOrder?.totalCost,
              name: clientName,
              email: clientEmail,
              customerNotes: clientCustomerNotes,
              dietaryRequirements: customerPreOrder?.dietaryRequirements,
              disableCreditCards: creditCardsDisabled,
            }}
            freedomPayProps={{
              action: "place_your_order",
              bookingId: bookingData.bookingId,
              bookingData,
              onFinish: () => {},
              pendingPreorderId: customerPreOrder?.pendingPreorderId,
              amount:
                customerPreOrder?.requiredPaymentGross ??
                customerPreOrder?.requiredPaymentAmount ??
                customerPreOrder?.total_gross ??
                customerPreOrder?.totalCost,
              onPaymentSuccess,
              name: clientName,
              email: clientEmail,
              customerNotes: clientCustomerNotes,
            }}
          />
        </>
      );
    }

    return (
      <PrimaryButton
        text={t("complete_preorder", "Complete Details to Continue")}
        className={
          "gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button gcw:w-full"
        }
        onClick={isValidDetails ? handleConfirmDetails : () => setShowValidationErrors(true)}
        disabled={confirmDisabled}
      />
    );
  };

  return (
    <div id="gc-confirm-details" className="app-main full-height">
      <div className="gcw:mb-2">
        <h1 className="payment-title">{t("confirm_your_details", "Confirm Your Details")}</h1>
        <div className="pre-order-info">
          <span className="kcal">
            {t("confirm_details_instruction", "Please confirm your name and email to continue.")}
          </span>
        </div>
      </div>
      <div className="gcw:flex">
        <div className="gcw:w-full gcw:flex gcw:flex-col">
          {showValidationErrors && errorMessages.length > 0 && (
            <AlertBanner
              variant="danger"
              icon={<ExclamationCircle color="#9a1212ff" size={16} />}
              className="gcw:mb-bs-3 gcw:w-full"
            >
              <div className="gcw:flex gcw:flex-col gcw:items-start">
                {errorMessages.map((msg) => (
                  <span key={msg}>{msg}</span>
                ))}
              </div>
            </AlertBanner>
          )}
          {submitError && (
            <AlertBanner
              variant="danger"
              icon={<ExclamationCircle color="#9a1212ff" size={16} />}
              className="gcw:mb-bs-3 gcw:w-full"
            >
              <span>
                {submitError}{" "}
                <button type="button" className="gcw:underline" onClick={() => window.location.reload()}>
                  {t("reload_page", "Reload the page")}
                </button>
              </span>
            </AlertBanner>
          )}
          {paidByGroupMember &&
            customerPreOrder?.requiredPaymentAmount &&
            customerPreOrder.requiredPaymentAmount > 0 && (
              <AlertBanner
                variant="warning"
                icon={<InfoCircle color="#9a3412" size={16} />}
                className="widget-panel-notice gcw:mb-bs-3 gcw:w-full"
              >
                <Trans
                  i18nKey="payment_now_required_confirm"
                  values={{
                    amount: formatCurrency(
                      customerPreOrder.requiredPaymentGross ?? customerPreOrder.requiredPaymentAmount,
                      customerPreOrder.booking.venue.currency ?? "GBP",
                      customerPreOrder.booking.venue.country_code ?? "GB",
                    ),
                  }}
                  components={{ strong: <strong /> }}
                />
              </AlertBanner>
            )}
          {numMismatchedPriceRules > 0 && (
            <AlertBanner
              variant="info"
              icon={<InfoCircle color="#1e3a5f" size={16} />}
              className="gcw:bg-light-purple gcw:border-medium-grey gcw:mb-bs-3 gcw:w-full gcw:!p-0"
            >
              <span>
                {t(
                  "fixed_price_confirm_add_more",
                  "Some of the fixed price menus added above will allow you to choose more items than you have currently selected, for the same price.",
                )}
              </span>
            </AlertBanner>
          )}
          {!showPaymentForm && (
            <>
              {showNotesField ? (
                <div className="gcw:mb-bs-3 gcw:w-full">
                  <div className="gcw:flex gcw:justify-start gcw:items-center gcw:mb-2">
                    <span className="gcw:mb-0 gcw:font-bold gcw:text-body">{t("order_notes", "Order notes")}</span>
                    <PrimaryButton
                      text={t("cancel", "Cancel")}
                      className={"btn-notes gcw:ms-2"}
                      onClick={() => {
                        setShowNotesField(false);
                        setClientCustomerNotes("");
                        onCustomerNotesChange?.("");
                      }}
                    />
                  </div>
                  <textarea
                    className="gcw:w-full"
                    aria-label={t("order_notes", "Order notes")}
                    maxLength={CUSTOMER_NOTES_MAX_LENGTH}
                    style={{
                      minHeight: "80px",
                      borderRadius: "8px",
                      border: "1px solid #D8DBDF",
                      padding: "10px",
                      fontSize: "1rem",
                      resize: "vertical",
                    }}
                    value={clientCustomerNotes}
                    onChange={(e) => {
                      setClientCustomerNotes(e.target.value);
                      onCustomerNotesChange?.(e.target.value);
                    }}
                    onFocus={(e) => {
                      e.target.style.border = "1.5px solid #007A7A";
                      e.target.style.outline = "none";
                    }}
                    onBlur={(e) => {
                      e.target.style.border = "1px solid #D8DBDF";
                    }}
                  />
                </div>
              ) : (
                <div className="gcw:mb-bs-3 gcw:w-full">
                  <div className="gcw:flex gcw:justify-start gcw:items-center">
                    <PrimaryButton
                      text={t("add_notes_about_order", "Add notes about your order.")}
                      className={"btn-notes"}
                      onClick={() => {
                        setShowNotesField(true);
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="customer-information gcw:w-full">
                <TextInput
                  type="text"
                  className="gcw:w-full gcw:mb-bs-3"
                  labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
                  id="name"
                  name="name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  label={t("your_name", "Your Name")}
                  placeholder={t("your_name", "Your Name")}
                  required
                />

                <TextInput
                  type="email"
                  className="gcw:w-full gcw:mb-bs-3"
                  labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary gcw:mb-2"
                  id="email"
                  name="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  label={t("email_address", "Email Address")}
                  placeholder={t("email_address", "Email Address")}
                  required
                />
              </div>
            </>
          )}
          <div
            id="gc-confirm-actions"
            className="gcw:flex gcw:justify-between gcw:items-center gcw:w-full gcw:gap-bs-3"
          >
            <div className="gcw:grow gcw:w-full">{renderActionButton()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmClientDetails;
